import type { LiveEvent, LiveUser } from "../../live/protocol";

export type BattleTeam = "ember" | "aqua";
export type BattleGift = "reinforce" | "artillery" | "flood" | "catrush" | "mech" | "rift" | "hound" | "tornado";

export type BattleAction = {
  id: string;
  team: BattleTeam;
  user: LiveUser;
  at: number;
} & (
  | { kind: "join" }
  | { kind: "charge"; amount: number }
  | { kind: "guard"; amount: number }
  | { kind: "support"; likes: number }
  | { kind: "follow" }
  | { kind: "gift"; gift: BattleGift; count: number; value: number }
);

const EMBER_WORDS = ["红", "红队", "火", "火队", "ember"];
const AQUA_WORDS = ["蓝", "蓝队", "水", "水队", "aqua"];

/** Maps platform-neutral live events into game-domain orders and owns stable viewer teams. */
export class BattleInteractionRouter {
  readonly #teams = new Map<string, BattleTeam>();
  readonly #listeners = new Set<(action: BattleAction) => void>();

  subscribe(listener: (action: BattleAction) => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  route(event: LiveEvent): BattleAction | null {
    let team = this.#teams.get(event.user.id) ?? teamFromId(event.user.id);
    let action: BattleAction | null = null;
    if (event.type === "comment") {
      const command = normalize(event.text);
      if (EMBER_WORDS.includes(command)) {
        team = "ember";
        this.#teams.set(event.user.id, team);
        action = { kind: "join", id: event.id, team, user: event.user, at: event.at };
      } else if (AQUA_WORDS.includes(command)) {
        team = "aqua";
        this.#teams.set(event.user.id, team);
        action = { kind: "join", id: event.id, team, user: event.user, at: event.at };
      } else if (command.includes("守") || command.includes("盾")) {
        action = { kind: "guard", amount: 8, id: event.id, team, user: event.user, at: event.at };
      } else if (command.includes("冲") || command.includes("打") || command.includes("进攻")) {
        action = { kind: "charge", amount: 7, id: event.id, team, user: event.user, at: event.at };
      }
    } else if (event.type === "like") {
      action = { kind: "support", likes: event.count, id: event.id, team, user: event.user, at: event.at };
    } else if (event.type === "follow") {
      action = { kind: "follow", id: event.id, team, user: event.user, at: event.at };
    } else {
      action = {
        kind: "gift",
        gift: classifyGift(event.giftId, event.value),
        count: event.count,
        value: event.value,
        id: event.id,
        team,
        user: event.user,
        at: event.at,
      };
    }
    if (!this.#teams.has(event.user.id)) this.#teams.set(event.user.id, team);
    if (action) for (const listener of this.#listeners) listener(action);
    return action;
  }

  teamFor(userId: string): BattleTeam {
    return this.#teams.get(userId) ?? teamFromId(userId);
  }

  reset(): void {
    this.#teams.clear();
  }
}

export function classifyGift(giftId: string, value: number): BattleGift {
  const id = giftId.toLocaleLowerCase("zh-CN");
  if (id.includes("flood") || id.includes("tide") || id.includes("tsunami")) return "flood";
  if (id.includes("mango") || id.includes("cat-rush") || id.includes("catrush") || id.includes("stampede")) return "catrush";
  if (id.includes("biped") || id.includes("mech")) return "mech";
  if (id.includes("quadruped") || id.includes("robo-hound") || id.includes("robohound")) return "hound";
  if (id.includes("rift") || id.includes("quake") || id.includes("earth")) return "rift";
  if (id.includes("tornado") || id.includes("storm") || value >= 1_000) return "tornado";
  if (value >= 700) return "rift";
  if (value >= 500) return "flood";
  if (id.includes("artillery") || id.includes("cannon") || value >= 300) return "artillery";
  return "reinforce";
}

function normalize(text: string): string {
  return text.trim().toLocaleLowerCase("zh-CN").replace(/[!！。,.，\s]/g, "");
}

function teamFromId(value: string): BattleTeam {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) % 2 === 0 ? "ember" : "aqua";
}
